import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  alpha,
  IconButton,
  Tooltip,
  Switch,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
} from "@mui/material";
import {
  AccountBalanceWallet as WalletIcon,
  Edit as EditIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import apiService from "../../services/apiService";
import { crmColors } from "../../theme/colors";

const WA = crmColors.whatsapp;
const WA_DARK = "#1DA851";
const RADIUS = "8px";
const PLAN_CARD_RADIUS = "10px";

const CATEGORY_ORDER = ["utility", "service", "authentication", "marketing"];

const CATEGORY_META = {
  utility: { color: crmColors.primary.main, label: "Utility" },
  service: { color: crmColors.success.main, label: "Service" },
  authentication: { color: crmColors.warning.main, label: "Authentication" },
  marketing: { color: crmColors.energy.main, label: "Marketing" },
};

const CATEGORIES = CATEGORY_ORDER.map((value) => ({
  value,
  label: CATEGORY_META[value].label,
}));

const PLAN_TYPES = [
  { value: "USAGE_BASED", label: "Usage based" },
  { value: "MONTHLY", label: "Monthly" },
];

const PLAN_VALIDITY = [
  { value: "UNLIMITED", label: "Unlimited" },
  { value: "MONTHLY", label: "Monthly" },
];

const formatMoney = (n, digits = 2) => {
  const num = Number(n) || 0;
  return `₹${num.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
};

const emptyEdit = {
  id: null,
  plan_id: "",
  plan_type: "USAGE_BASED",
  plan_name: "",
  conversation_category: "utility",
  plan_value: "",
  plan_validity: "UNLIMITED",
  included_messages: "",
  overage_price_per_message: "",
  meta_cost_reference: "",
  is_active: true,
};

const planValueOf = (plan) => Number(plan?.plan_value ?? plan?.monthly_fee) || 0;

const MetricPill = memo(function MetricPill({ label, value, color }) {
  return (
    <Box
      sx={{
        px: 0.85,
        py: 0.35,
        borderRadius: "6px",
        bgcolor: alpha(color, 0.09),
        border: `1px solid ${alpha(color, 0.16)}`,
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.58rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: alpha(color, 0.95),
          lineHeight: 1.1,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", color: crmColors.text.primary, lineHeight: 1.25 }}>
        {value}
      </Typography>
    </Box>
  );
});

const PlanCard = memo(function PlanCard({ plan, editable, statusUpdating, onEdit, onToggleStatus }) {
  const category = String(plan.conversation_category || "").toLowerCase();
  const meta = CATEGORY_META[category] || { color: crmColors.secondary.main, label: category || "—" };
  const overage = Number(plan.overage_price_per_message ?? plan.price_per_message) || 0;
  const metaCost = Number(plan.meta_cost_reference) || 0;
  const active = plan.is_active === true || plan.is_active === 1 || plan.is_active === "1";
  const included = Number(plan.included_messages) || 0;

  return (
    <Paper
      elevation={0}
      sx={{
        position: "relative",
        overflow: "hidden",
        borderRadius: PLAN_CARD_RADIUS,
        border: `1px solid ${alpha(meta.color, active ? 0.28 : 0.14)}`,
        background: active
          ? `linear-gradient(145deg, ${alpha(meta.color, 0.07)} 0%, #fff 42%)`
          : alpha(crmColors.text.secondary, 0.03),
        opacity: active ? 1 : 0.72,
        transition: "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
        "&:hover": {
          borderColor: alpha(meta.color, 0.45),
          boxShadow: `0 8px 20px -14px ${alpha(meta.color, 0.55)}`,
          transform: "translateY(-1px)",
        },
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: meta.color,
          opacity: active ? 1 : 0.4,
        },
      }}
    >
      <Box sx={{ pl: 1.5, pr: 1.25, py: 1.1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={0.75}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 800, fontSize: "0.92rem", lineHeight: 1.2, color: crmColors.text.primary }}>
                {plan.plan_name || "Untitled"}
              </Typography>
              <Chip
                size="small"
                label={plan.plan_id || "—"}
                sx={{
                  height: 18,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  bgcolor: alpha(meta.color, 0.12),
                  color: meta.color,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Stack>
            <Typography
              sx={{
                mt: 0.35,
                fontWeight: 800,
                fontSize: "1.15rem",
                letterSpacing: "-0.02em",
                color: crmColors.text.primary,
                lineHeight: 1.1,
              }}
            >
              {formatMoney(planValueOf(plan), 0)}
              <Typography component="span" sx={{ ml: 0.5, fontSize: "0.7rem", fontWeight: 600, color: "text.secondary" }}>
                {String(plan.plan_validity || "").toUpperCase() === "MONTHLY" ? "/mo" : ""}
              </Typography>
            </Typography>
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.15} sx={{ flexShrink: 0 }}>
            {editable ? (
              <>
                {statusUpdating ? (
                  <CircularProgress size={14} sx={{ color: WA, mx: 0.5 }} />
                ) : (
                  <Switch size="small" checked={active} onChange={() => onToggleStatus(plan)} color="success" />
                )}
                <Tooltip title="Edit plan">
                  <IconButton
                    size="small"
                    onClick={() => onEdit(plan)}
                    sx={{
                      p: 0.55,
                      bgcolor: alpha(meta.color, 0.1),
                      border: `1px solid ${alpha(meta.color, 0.2)}`,
                      "&:hover": { bgcolor: alpha(meta.color, 0.18) },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 15, color: meta.color }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Chip
                size="small"
                label={active ? "Active" : "Off"}
                color={active ? "success" : "default"}
                sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={0.6} sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
          <MetricPill
            label="Msgs"
            value={included > 0 ? included.toLocaleString() : "Usage"}
            color={meta.color}
          />
          <MetricPill
            label="Validity"
            value={String(plan.plan_validity || "UNLIMITED").replace("_", " ")}
            color={crmColors.primary.main}
          />
          <MetricPill label="Overage" value={formatMoney(overage, 4)} color={crmColors.energy.main} />
          <MetricPill label="Meta" value={formatMoney(metaCost, 4)} color={crmColors.secondary.main} />
        </Stack>
      </Box>
    </Paper>
  );
});

/**
 * Displays EduOK Tech Provider plans from tm_whatsapp_subscription_plans only.
 */
const WhatsAppPricePlans = ({
  plans: plansProp = null,
  title = "Price plans",
  subtitle = "School packages by category",
  activeOnly = false,
  showHeader = true,
  editable = true,
  openCreateSignal = 0,
  refreshSignal = 0,
}) => {
  const [plans, setPlans] = useState(Array.isArray(plansProp) ? plansProp : []);
  const [loading, setLoading] = useState(!Array.isArray(plansProp));
  const [saving, setSaving] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("edit");
  const [editForm, setEditForm] = useState(emptyEdit);
  const [selectedCategory, setSelectedCategory] = useState("utility");

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    const result = await apiService.getWhatsAppPricePlans({ activeOnly: false });
    if (result.success) {
      const data = result.data || {};
      const list = Array.isArray(data.plans)
        ? data.plans
        : Array.isArray(data.subscription_plans)
          ? data.subscription_plans
          : Array.isArray(data)
            ? data
            : [];
      setPlans(list);
    } else {
      toast.error(result.error || "Failed to load plans");
      setPlans([]);
    }
    setLoading(false);
  }, []);

  const openCreate = useCallback(
    (category) => {
      const preferred = typeof category === "string" ? category : null;
      const cat =
        (preferred && CATEGORY_META[preferred] && preferred) ||
        (CATEGORY_META[selectedCategory] && selectedCategory) ||
        "utility";
      setDialogMode("create");
      setEditForm({
        ...emptyEdit,
        conversation_category: cat,
        plan_value: "0",
        plan_validity: "UNLIMITED",
        included_messages: "1000",
        overage_price_per_message: "0.10",
        meta_cost_reference: "0",
      });
      setEditOpen(true);
    },
    [selectedCategory]
  );

  useEffect(() => {
    if (Array.isArray(plansProp) && !editable) {
      setPlans(plansProp);
      setLoading(false);
      return;
    }
    fetchPlans();
  }, [plansProp, editable, fetchPlans]);

  useEffect(() => {
    if (!refreshSignal) return;
    fetchPlans();
  }, [refreshSignal, fetchPlans]);

  useEffect(() => {
    if (!openCreateSignal) return;
    openCreate(selectedCategory);
  }, [openCreateSignal, openCreate, selectedCategory]);

  const categoryOptions = useMemo(() => {
    const counts = {};
    plans.forEach((p) => {
      const key = String(p.conversation_category || "").toLowerCase().trim();
      if (!key) return;
      counts[key] = (counts[key] || 0) + 1;
    });
    const known = CATEGORY_ORDER.map((value) => ({
      value,
      label: CATEGORY_META[value].label,
      color: CATEGORY_META[value].color,
      count: counts[value] || 0,
    }));
    const extras = Object.keys(counts)
      .filter((k) => !CATEGORY_ORDER.includes(k))
      .sort()
      .map((value) => ({
        value,
        label: value.charAt(0).toUpperCase() + value.slice(1),
        color: crmColors.secondary.main,
        count: counts[value] || 0,
      }));
    return [...known, ...extras];
  }, [plans]);

  const plansByCategory = useMemo(() => {
    const used = new Set();
    const groups = categoryOptions.map((opt) => {
      const groupPlans = plans
        .filter((p) => {
          const cat = String(p.conversation_category || "").toLowerCase().trim();
          const match = cat === opt.value;
          if (match) {
            used.add(p.id != null && p.id !== "" ? `id:${p.id}` : `row:${cat}:${p.plan_name}`);
          }
          return match;
        })
        .sort((a, b) => {
          const feeDiff = planValueOf(a) - planValueOf(b);
          if (feeDiff !== 0) return feeDiff;
          return String(a.plan_name || "").localeCompare(String(b.plan_name || ""));
        });
      return { ...opt, plans: groupPlans };
    });

    const orphans = plans.filter((p) => {
      const cat = String(p.conversation_category || "").toLowerCase().trim();
      const key = p.id != null && p.id !== "" ? `id:${p.id}` : `row:${cat}:${p.plan_name}`;
      return !used.has(key);
    });
    if (orphans.length > 0) {
      groups.push({
        value: "other",
        label: "Other",
        color: crmColors.secondary.main,
        count: orphans.length,
        plans: orphans,
      });
    }
    return groups.filter((g) => g.plans.length > 0 || CATEGORY_ORDER.includes(g.value));
  }, [plans, categoryOptions]);

  const summary = useMemo(() => {
    const activeCount = plans.filter((p) => p.is_active === true || p.is_active === 1 || p.is_active === "1").length;
    return { total: plans.length, activeCount };
  }, [plans]);

  const openEdit = (plan) => {
    setDialogMode("edit");
    setEditForm({
      id: plan.id,
      plan_id: plan.plan_id || "",
      plan_type: plan.plan_type || "USAGE_BASED",
      plan_name: plan.plan_name || "",
      conversation_category: String(plan.conversation_category || "utility").toLowerCase(),
      plan_value: String(plan.plan_value ?? plan.monthly_fee ?? ""),
      plan_validity: plan.plan_validity || "UNLIMITED",
      included_messages: String(plan.included_messages ?? ""),
      overage_price_per_message: String(plan.overage_price_per_message ?? plan.price_per_message ?? ""),
      meta_cost_reference: String(plan.meta_cost_reference ?? ""),
      is_active: plan.is_active === true || plan.is_active === 1 || plan.is_active === "1",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.plan_name.trim()) {
      toast.error("Plan name is required");
      return;
    }
    if (editForm.overage_price_per_message === "" || Number.isNaN(Number(editForm.overage_price_per_message))) {
      toast.error("Overage price is required");
      return;
    }
    const payload = {
      plan_name: editForm.plan_name.trim(),
      plan_value: Number(editForm.plan_value || 0),
      plan_validity: editForm.plan_validity,
      included_messages: editForm.included_messages === "" ? null : Number(editForm.included_messages),
      overage_price_per_message: Number(editForm.overage_price_per_message),
      meta_cost_reference: Number(editForm.meta_cost_reference || 0),
      is_active: editForm.is_active,
    };
    if (dialogMode === "create") {
      payload.plan_id = editForm.plan_id.trim() || undefined;
      payload.plan_type = editForm.plan_type;
      payload.conversation_category = editForm.conversation_category;
    }
    setSaving(true);
    const result =
      dialogMode === "create"
        ? await apiService.createWhatsAppPricePlan(payload)
        : await apiService.updateWhatsAppPricePlan({ id: editForm.id, ...payload });
    setSaving(false);
    if (result.success) {
      toast.success(dialogMode === "create" ? "Plan created" : "Plan updated");
      setEditOpen(false);
      fetchPlans();
    } else {
      toast.error(result.error || (dialogMode === "create" ? "Failed to create plan" : "Failed to update plan"));
    }
  };

  const handleToggleStatus = async (plan) => {
    const nextActive = !(plan.is_active === true || plan.is_active === 1 || plan.is_active === "1");
    setStatusUpdatingId(plan.id);
    const result = await apiService.updateWhatsAppPricePlan({
      id: plan.id,
      is_active: nextActive,
    });
    setStatusUpdatingId(null);
    if (result.success) {
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, is_active: nextActive } : p)));
      fetchPlans();
    } else {
      toast.error(result.error || "Failed to update status");
    }
  };

  const editCategoryColor = CATEGORY_META[editForm.conversation_category]?.color || WA;
  const fieldSx = {
    "& .MuiInputBase-root": { fontSize: "0.875rem", borderRadius: "8px" },
    "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  };

  return (
    <>
      <Stack spacing={1.5}>
        {showHeader && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", color: crmColors.text.primary }}>{title}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {subtitle}
              </Typography>
            </Box>
            {editable && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => openCreate(selectedCategory)}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: RADIUS,
                  bgcolor: WA,
                  boxShadow: `0 6px 14px -8px ${alpha(WA, 0.9)}`,
                  "&:hover": { bgcolor: WA_DARK },
                }}
              >
                Add
              </Button>
            )}
          </Stack>
        )}

        {!loading && plans.length > 0 && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{
              px: 1.1,
              py: 0.7,
              borderRadius: RADIUS,
              bgcolor: alpha(WA, 0.06),
              border: `1px solid ${alpha(WA, 0.14)}`,
              width: "fit-content",
            }}
          >
            <Chip
              size="small"
              label={`${summary.total} plans`}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.7rem",
                bgcolor: "#fff",
                border: `1px solid ${alpha(WA, 0.2)}`,
              }}
            />
            <Chip
              size="small"
              label={`${summary.activeCount} active`}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: "0.7rem",
                bgcolor: alpha(crmColors.success.main, 0.14),
                color: crmColors.success.main,
              }}
            />
            <Chip
              size="small"
              label={`${Math.max(0, summary.total - summary.activeCount)} inactive`}
              variant="outlined"
              sx={{ height: 22, fontWeight: 700, fontSize: "0.7rem" }}
            />
          </Stack>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress size={28} sx={{ color: WA }} />
          </Box>
        ) : plans.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              py: 4,
              px: 2,
              textAlign: "center",
              borderRadius: RADIUS,
              border: `1px dashed ${alpha(WA, 0.35)}`,
              background: `linear-gradient(160deg, ${alpha(WA, 0.08)} 0%, #fff 60%)`,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                mx: "auto",
                mb: 1,
                borderRadius: "10px",
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(WA, 0.12),
              }}
            >
              <WalletIcon sx={{ fontSize: 24, color: WA_DARK }} />
            </Box>
            <Typography fontWeight={800} fontSize="0.95rem">
              No plans found
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35 }}>
              Create a school package to get started
            </Typography>
            {editable && (
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openCreate("utility")}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: RADIUS,
                  bgcolor: WA,
                  "&:hover": { bgcolor: WA_DARK },
                }}
              >
                Add plan
              </Button>
            )}
          </Paper>
        ) : (
          <Stack spacing={1.35}>
            {plansByCategory.map((group) => (
              <Paper
                key={group.value}
                elevation={0}
                sx={{
                  borderRadius: RADIUS,
                  overflow: "hidden",
                  border: `1px solid ${alpha(group.color, 0.22)}`,
                  bgcolor: "#fff",
                  boxShadow: `0 10px 24px -20px ${alpha(group.color, 0.55)}`,
                }}
              >
                <Box
                  sx={{
                    px: 1.35,
                    py: 0.85,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    background: `linear-gradient(90deg, ${alpha(group.color, 0.16)} 0%, ${alpha(group.color, 0.04)} 100%)`,
                    borderBottom: `1px solid ${alpha(group.color, 0.12)}`,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: "6px",
                        bgcolor: group.color,
                        color: "#fff",
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        display: "grid",
                        placeItems: "center",
                        boxShadow: `0 4px 10px -4px ${alpha(group.color, 0.9)}`,
                      }}
                    >
                      {group.label.charAt(0)}
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.88rem", color: group.color, letterSpacing: "-0.01em" }}>
                      {group.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={group.plans.length}
                      sx={{
                        height: 18,
                        minWidth: 22,
                        fontSize: "0.65rem",
                        fontWeight: 800,
                        bgcolor: alpha(group.color, 0.14),
                        color: group.color,
                        "& .MuiChip-label": { px: 0.7 },
                      }}
                    />
                  </Stack>
                  {editable && (
                    <Button
                      size="small"
                      startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                      onClick={() => {
                        setSelectedCategory(group.value);
                        openCreate(group.value);
                      }}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        minHeight: 26,
                        py: 0,
                        px: 1,
                        borderRadius: "6px",
                        color: group.color,
                        bgcolor: alpha(group.color, 0.08),
                        "&:hover": { bgcolor: alpha(group.color, 0.16) },
                      }}
                    >
                      Add
                    </Button>
                  )}
                </Box>

                <Box sx={{ p: 1.1 }}>
                  {group.plans.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 0.75, py: 1, display: "block", fontWeight: 600 }}
                    >
                      No plans in this category yet
                    </Typography>
                  ) : (
                    <Grid container spacing={1.1}>
                      {group.plans.map((plan, idx) => (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          lg={4}
                          key={plan.id ? `${group.value}-${plan.id}` : `${group.value}-${plan.plan_name}-${idx}`}
                        >
                          <PlanCard
                            plan={plan}
                            editable={editable}
                            statusUpdating={statusUpdatingId === plan.id}
                            onEdit={openEdit}
                            onToggleStatus={handleToggleStatus}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Stack>

      <Dialog
        open={editOpen}
        onClose={() => !saving && setEditOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: RADIUS,
            overflow: "hidden",
            boxShadow: `0 18px 40px -24px ${alpha(editCategoryColor, 0.7)}`,
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.35,
            background: `linear-gradient(135deg, ${alpha(editCategoryColor, 0.18)} 0%, ${alpha(editCategoryColor, 0.05)} 100%)`,
            borderBottom: `1px solid ${alpha(editCategoryColor, 0.14)}`,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: crmColors.text.primary }}>
            {dialogMode === "create" ? "Add plan" : "Edit plan"}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {dialogMode === "create"
              ? "Set package pricing and overage rate"
              : "Update fee, messages, and rates"}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 1.75, px: 2, pb: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <TextField
              size="small"
              label="Plan name *"
              value={editForm.plan_name}
              onChange={(e) => setEditForm((p) => ({ ...p, plan_name: e.target.value }))}
              fullWidth
              sx={fieldSx}
            />
            <Grid container spacing={1.25}>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  label="Plan ID"
                  value={editForm.plan_id}
                  onChange={(e) => setEditForm((p) => ({ ...p, plan_id: e.target.value }))}
                  fullWidth
                  placeholder="Auto"
                  disabled={dialogMode === "edit"}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" sx={fieldSx} disabled={dialogMode === "edit"}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={editForm.plan_type}
                    onChange={(e) => setEditForm((p) => ({ ...p, plan_type: e.target.value }))}
                  >
                    {PLAN_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small" sx={fieldSx} disabled={dialogMode === "edit"}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    label="Category"
                    value={editForm.conversation_category}
                    onChange={(e) => setEditForm((p) => ({ ...p, conversation_category: e.target.value }))}
                  >
                    {CATEGORIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  label="Plan value"
                  type="number"
                  inputProps={{ step: "1", min: "0" }}
                  value={editForm.plan_value}
                  onChange={(e) => setEditForm((p) => ({ ...p, plan_value: e.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" sx={fieldSx}>
                  <InputLabel>Validity</InputLabel>
                  <Select
                    label="Validity"
                    value={editForm.plan_validity}
                    onChange={(e) => setEditForm((p) => ({ ...p, plan_validity: e.target.value }))}
                  >
                    {PLAN_VALIDITY.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  label="Included msgs"
                  type="number"
                  inputProps={{ step: "1", min: "0" }}
                  value={editForm.included_messages}
                  onChange={(e) => setEditForm((p) => ({ ...p, included_messages: e.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  label="Overage *"
                  type="number"
                  inputProps={{ step: "0.0001", min: "0" }}
                  value={editForm.overage_price_per_message}
                  onChange={(e) => setEditForm((p) => ({ ...p, overage_price_per_message: e.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  size="small"
                  label="Meta cost"
                  type="number"
                  inputProps={{ step: "0.0001", min: "0" }}
                  value={editForm.meta_cost_reference}
                  onChange={(e) => setEditForm((p) => ({ ...p, meta_cost_reference: e.target.value }))}
                  fullWidth
                  sx={fieldSx}
                />
              </Grid>
            </Grid>
            <Paper
              elevation={0}
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: RADIUS,
                border: `1px solid ${alpha(crmColors.success.main, editForm.is_active ? 0.28 : 0.12)}`,
                bgcolor: alpha(crmColors.success.main, editForm.is_active ? 0.08 : 0.03),
              }}
            >
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={editForm.is_active}
                    onChange={(e) => setEditForm((p) => ({ ...p, is_active: e.target.checked }))}
                    color="success"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={700}>
                    {editForm.is_active ? "Active" : "Inactive"}
                  </Typography>
                }
              />
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.75, pt: 0.5, gap: 0.5 }}>
          <Button size="small" onClick={() => setEditOpen(false)} disabled={saving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSaveEdit}
            disabled={saving}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: RADIUS,
              bgcolor: WA,
              boxShadow: `0 6px 14px -8px ${alpha(WA, 0.9)}`,
              "&:hover": { bgcolor: WA_DARK },
            }}
          >
            {saving ? "Saving…" : dialogMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default memo(WhatsAppPricePlans);
